import { copyFor } from "./next-steps-copy"
import type { DraftReviewReport, GateDefectView } from "./review"

/**
 * WHAT THE OFFICER STILL OWES THIS ORDER — the adjudication flow's next steps.
 *
 * Every draft is checked by some thirty deterministic gates and read once by an
 * appellate-eye reviewer. Until 21 Sep 2026 the worst of those findings
 * WITHHELD the order (the officer got a failure panel) and the rest shipped in a
 * review layer no screen rendered. The order is always delivered now; this is
 * where what the checks found reaches the person who has to sign it, as things
 * he can act on: see the passage, have it fixed (the rewrite chat, which asks
 * his approval before it changes a word), or keep the draft as it is — his
 * call, recorded.
 *
 * LIGHT ON PURPOSE. The cards are a projection of the review layer the draft
 * already produced; nothing is re-run when one is settled, and "settled" is the
 * officer's own mark on the case (thread metadata), not a verdict of the engine.
 * The appellate flow's cards change a decision record and start a revise; these
 * do not, yet.
 */
export type OioStepTier = "must" | "should" | "look"

export interface OioNextStep {
  /** Stable for the same finding across reloads; a redraft that changes the finding is a new step. */
  id: string
  tier: OioStepTier
  /** One line, in words — never a gate code. */
  title: string
  /** Why it matters and what to do, in the officer's words — behind "Details". */
  detail: string
  /** The exact words in the order the check stopped at ("₹", "[to be recorded]"), when it quoted them. */
  found: string
  /** "5.2", or "" when it is about the order as a whole. */
  section: string
  source: "gate" | "reviewer" | "citations"
  /** How many times the same finding was made in the same section (one card, not three). */
  count: number
}

export type OioStepDecision = "resolved" | "kept"

export const TIER_LABEL: Record<OioStepTier, string> = {
  must: "Settle before signing",
  should: "Should fix",
  look: "Worth a look",
}

const TIER_OF: Record<GateDefectView["severity"], OioStepTier> = { fatal: "must", error: "should", warning: "look" }
const TIER_ORDER: Record<OioStepTier, number> = { must: 0, should: 1, look: 2 }

/** FNV-1a: a short stable id from the finding's own words (no crypto needed). */
function hash(text: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(36)
}

/**
 * A finding in words: the officer's wording written for it (next-steps-copy.ts).
 * Should a check ever reach here without one, the requirements checklist's
 * title stands in, then a plain sentence — NEVER the check's own message, which
 * is written for the drafting loop ("Forbidden token in the signed order…") and
 * is how "token" once reached an officer's screen.
 */
function wordsFor(defect: GateDefectView, report: DraftReviewReport): { title: string; detail: string } {
  const copy = copyFor(defect.code)
  if (copy) return { title: copy.title, detail: copy.why }
  const family = defect.code.split(":")[0]
  const claimed = report.requirements.find((r) =>
    r.defect_codes.some((c) => c === defect.code || c.split(":")[0] === family),
  )
  return {
    title: claimed?.title || "A check on this order did not pass",
    detail: "Open the section shown and read it against the notice and the reply before signing.",
  }
}

/** What the check quoted from the order: the first '…' in its message. */
function quotedFrom(message: string): string {
  const match = /'([^']{1,200})'/.exec(message)
  return match ? match[1].trim() : ""
}

function sectionSort(section: string): number[] {
  return section ? section.split(".").map((n) => Number(n) || 0) : [Number.MAX_SAFE_INTEGER]
}

export function buildOioNextSteps(report: DraftReviewReport | null): OioNextStep[] {
  if (!report) return []
  const steps: OioNextStep[] = []
  const seen = new Map<string, OioNextStep>()
  for (const d of report.gate_defects) {
    // The same finding made three times in one section is ONE thing to settle.
    const id = `gate-${hash(`${d.code}|${d.section_number}|${d.message}`)}`
    const already = seen.get(id)
    if (already) {
      already.count++
      continue
    }
    const words = wordsFor(d, report)
    const step: OioNextStep = {
      id,
      tier: TIER_OF[d.severity] ?? "look",
      title: words.title,
      detail: words.detail,
      found: quotedFrom(d.message),
      section: d.section_number ?? "",
      source: "gate",
      count: 1,
    }
    seen.set(id, step)
    steps.push(step)
  }
  for (const f of report.reviewer_findings) {
    // An "observation" is the reviewer thinking aloud; a "concern" is something
    // an appellate authority would put to the officer.
    if (f.severity !== "concern" || !f.claim) continue
    steps.push({
      id: `reviewer-${hash(`${f.section_number}|${f.claim}`)}`,
      tier: "look",
      title: f.claim,
      detail: "A second reading of the order, as an appellate authority would read it, raised this. Consider whether the order should meet it.",
      found: f.basis ?? "",
      section: f.section_number ?? "",
      source: "reviewer",
      count: 1,
    })
  }
  if (report.unverified_citation_count > 0) {
    const n = report.unverified_citation_count
    steps.push({
      id: `citations-${n}`,
      tier: "should",
      title: n === 1 ? "One cited judgment could not be verified" : `${n} cited judgments could not be verified`,
      detail: "Open Analysis to check each one against its source, then confirm it or remove it from the order.",
      found: "",
      section: "",
      source: "citations",
      count: 1,
    })
  }
  const bySection = (a: OioNextStep, b: OioNextStep) => {
    const x = sectionSort(a.section)
    const y = sectionSort(b.section)
    for (let i = 0; i < Math.max(x.length, y.length); i++) {
      if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) - (y[i] ?? 0)
    }
    return 0
  }
  return steps.sort((a, b) => TIER_ORDER[a.tier] - TIER_ORDER[b.tier] || bySection(a, b))
}

export function openSteps(steps: OioNextStep[], decisions: Record<string, OioStepDecision>): OioNextStep[] {
  return steps.filter((s) => !decisions[s.id])
}

/** Open "settle before signing" steps: while any remain, the order is not cleared (Export and Filed wait). */
export function mustSettleCount(steps: OioNextStep[], decisions: Record<string, OioStepDecision>): number {
  return openSteps(steps, decisions).filter((s) => s.tier === "must").length
}

/** The officer's marks on the case, from thread metadata; anything malformed is no mark at all. */
export function readDecisions(metadata: Record<string, unknown> | null | undefined): Record<string, OioStepDecision> {
  const raw = metadata?.oioNextSteps
  if (!raw || typeof raw !== "object") return {}
  const out: Record<string, OioStepDecision> = {}
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === "resolved" || value === "kept") out[id] = value
  }
  return out
}

/** What the rewrite chat is asked when the officer says "Fix with AI". */
export function fixInstruction(step: OioNextStep): string {
  const where = step.section ? `In section ${step.section} of the order: ` : ""
  const found = step.found ? ` The text is: “${step.found}”.` : ""
  return `${where}${step.title}. ${step.detail}${found} Please correct it and show me the change before it is saved.`
}
